import { useState, FormEvent } from 'react';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutStats, testimonials, faqData } from '@/mocks/videos';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('website_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');

    const name = (formData.get('name') as string || '').trim();
    const email = (formData.get('email') as string || '').trim();
    const message = (formData.get('message') as string || '').trim();

    if (!name || !email || !message) {
      setFormStatus('error');
      setFormError('请填写所有必填字段');
      return;
    }

    if (message.length > 500) {
      setFormStatus('error');
      setFormError('留言内容不能超过500个字符');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pmf0li2vhsdh2d37ag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name,
          email,
          message,
        }).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后再试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后再试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查您的连接后重试');
    }
  };

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于91视频网站',
    description: '91视频网站品牌介绍、用户口碑与联系方式',
    url: 'https://91video.example.com/about',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 91视频网站 | 品牌介绍与联系方式"
        description="了解91视频网站的品牌故事、用户口碑和联系方式。8年专注高清视频在线播放服务，服务超过350万日活用户，提供海量免费视频资源。"
        keywords="91视频网站,关于我们,联系方式,品牌介绍,91视频网站免费资源,高清视频网站推荐"
        canonicalUrl="https://91video.example.com/about"
        schema={{ ...pageSchema, ...faqSchema }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="py-8 md:py-12">
            <div className="text-center mb-12">
              <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 mb-4">关于91视频网站</h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                成立于2018年，91视频网站致力于为广大用户提供优质的高清视频在线观看服务。
                我们相信，好的内容应该被每个人轻松获取。
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
              {[
                { value: `${aboutStats.yearsRunning}年`, label: '专注视频服务' },
                { value: `${(aboutStats.totalVideos / 10000).toFixed(1)}万+`, label: '视频资源' },
                { value: `${(aboutStats.dailyActiveUsers / 10000).toFixed(0)}万+`, label: '日活用户' },
                { value: `${(aboutStats.totalPlayCount / 100000000).toFixed(1)}亿+`, label: '总播放量' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100 border border-background-200/60">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
              <div>
                <h2 className="text-xl font-heading text-foreground-900 mb-4">品牌故事</h2>
                <div className="text-sm text-foreground-600 leading-relaxed space-y-3">
                  <p>
                    91视频网站创立之初，我们的目标就很简单：让每个人都能轻松找到并观看自己喜欢的高清视频。
                  </p>
                  <p>
                    经过{aboutStats.yearsRunning}年的发展，我们已经成长为拥有超过{aboutStats.totalVideos.toLocaleString()}部视频资源的大型平台，
                    每天服务超过{aboutStats.dailyActiveUsers.toLocaleString()}万活跃用户，累计播放量突破{(aboutStats.totalPlayCount / 100000000).toFixed(1)}亿次。
                  </p>
                  <p>
                    从热门电影到经典剧集，从精彩综艺到优质纪录片，我们持续扩充内容库，
                    并不断优化播放技术，确保每一位用户都能获得流畅、高清的观看体验。
                  </p>
                  <p>
                    我们坚信，优质的内容不应该被高昂的会员费所限制。91视频网站始终坚持免费模式，
                    让更多人能够享受视频带来的快乐与知识。
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-heading text-foreground-900 mb-4">用户口碑</h2>
                <div className="space-y-4">
                  {testimonials.map((testimonial, index) => (
                    <div key={index} className="p-4 rounded-xl bg-background-100 border border-background-200/60">
                      <div className="flex items-center gap-1 mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            className={`text-sm ${i < testimonial.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                          ></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                        &ldquo;{testimonial.content}&rdquo;
                      </p>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-secondary-500 flex items-center justify-center text-white text-xs font-medium">
                          {testimonial.name.charAt(0)}
                        </div>
                        <div>
                          <span className="text-sm font-medium text-foreground-700">{testimonial.name}</span>
                          <span className="text-xs text-foreground-400 ml-2">{testimonial.role}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="py-8 md:py-12 border-t border-background-200/60">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 text-center mb-2">联系我们</h2>
              <p className="text-sm text-foreground-500 text-center mb-8">有任何问题或建议？我们很乐意听取您的意见</p>

              {formStatus === 'success' ? (
                <div className="text-center p-8 rounded-xl bg-primary-50 border border-primary-200">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2">提交成功！</h3>
                  <p className="text-sm text-foreground-500 mb-4">感谢您的留言，我们会尽快与您联系。</p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="px-5 py-2 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                  <div className="honeypot-field">
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名 *</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        placeholder="您的姓名"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱 *</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        placeholder="your@email.com"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容 *（最多500字）</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="请输入您的留言或建议..."
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                    ></textarea>
                  </div>

                  {formStatus === 'error' && formError && (
                    <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
                      <i className="ri-error-warning-line mr-1"></i>
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    发送留言
                  </button>
                </form>
              )}

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                {[
                  { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@91video.example.com' },
                  { icon: 'ri-customer-service-2-line', title: '客服热线', value: '400-888-9191' },
                  { icon: 'ri-map-pin-line', title: '公司地址', value: '北京市朝阳区光华路9号' },
                ].map((item) => (
                  <div key={item.title} className="p-4 rounded-xl bg-background-100">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <h4 className="text-sm font-medium text-foreground-700 mb-1">{item.title}</h4>
                    <p className="text-xs text-foreground-500">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-8 md:py-12 border-t border-background-200/60">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 text-center mb-8">常见问题</h2>
              <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
                {faqData.map((faq, index) => (
                  <div
                    key={index}
                    className="rounded-xl border border-background-200/60 overflow-hidden"
                    itemScope
                    itemProp="mainEntity"
                    itemType="https://schema.org/Question"
                  >
                    <button
                      onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-background-100 transition-colors"
                    >
                      <span className="text-sm font-medium text-foreground-800 pr-4" itemProp="name">{faq.question}</span>
                      <i
                        className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 flex-shrink-0 ${
                          faqOpen === index ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>
                    {faqOpen === index && (
                      <div
                        className="px-4 pb-4"
                        itemScope
                        itemProp="acceptedAnswer"
                        itemType="https://schema.org/Answer"
                      >
                        <p className="text-sm text-foreground-600 leading-relaxed" itemProp="text">
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}